import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { NoDataPlaceholder } from '../../../ComponnentsUI/common/NoDataPlaceholder';
import {
  ChartDatum,
  CountByDatePoint,
  HomeKpisCoverageItem,
  HomeKpisFacilityComparisonItem,
  HomeKpisGaPoint,
  HomeKpisHeatmapPoint,
  MonthlyCountsPoint,
} from '../../../interfaces/homeKpis';

const COLORS = [
  '#3B82F6',
  '#10B981',
  '#8B5CF6',
  '#F59E0B',
  '#EF4444',
  '#06B6D4',
];

const tooltipStyle = {
  backgroundColor: 'var(--tooltip-bg)',
  border: '1px solid var(--tooltip-border)',
  borderRadius: '0.5rem',
  fontSize: '12px',
  color: 'var(--tooltip-text)',
};

const tooltipClassName =
  '[--tooltip-bg:white] dark:[--tooltip-bg:rgb(31,41,55)] [--tooltip-border:#e5e7eb] dark:[--tooltip-border:rgb(75,85,99)] [--tooltip-text:rgb(75,85,99)] dark:[--tooltip-text:rgb(209,213,219)]';

export const MonthlyActivityChart: React.FC<{
  data: MonthlyCountsPoint[];
  patientsLabel: string;
  examsLabel: string;
}> = ({ data, patientsLabel, examsLabel }) => {
  const hasData = data?.some(
    (point) => point.patients > 0 || point.studies > 0
  );
  if (!hasData) {
    return <NoDataPlaceholder className="h-72" />;
  }

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 8, right: 18, left: -12, bottom: 0 }}
        >
          <defs>
            <linearGradient id="homeKpisStudies" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="homeKpisPatients" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.35} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            wrapperStyle={{ fontSize: '12px', paddingBottom: '12px' }}
          />
          <Area
            type="monotone"
            dataKey="patients"
            name={patientsLabel}
            stroke="#3B82F6"
            strokeWidth={2}
            fill="url(#homeKpisPatients)"
          />
          <Area
            type="monotone"
            dataKey="studies"
            name={examsLabel}
            stroke="#10B981"
            strokeWidth={2}
            fill="url(#homeKpisStudies)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const DailyCountChart: React.FC<{
  data: CountByDatePoint[];
  label: string;
  color?: string;
}> = ({ data, label, color = '#3B82F6' }) => {
  const hasData = data?.some((point) => point.count > 0);
  if (!hasData) {
    return <NoDataPlaceholder className="h-64" />;
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 8, right: 18, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Line
            type="monotone"
            dataKey="count"
            name={label}
            stroke={color}
            strokeWidth={2}
            dot={data.length < 45}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CoverageBarChart: React.FC<{
  data: HomeKpisCoverageItem[];
  coverageLabel: string;
  maxItems?: number;
}> = ({ data, coverageLabel, maxItems = 10 }) => {
  const rows = (data || []).slice(0, maxItems);
  if (!rows.length) {
    return <NoDataPlaceholder className="h-80" />;
  }

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={rows}
          margin={{ top: 4, right: 20, left: 68, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            type="number"
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
            unit="%"
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            width={128}
            axisLine={false}
            tickLine={false}
            className="text-gray-500 dark:text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
            formatter={(value: number) => [`${value}%`, coverageLabel]}
          />
          <Bar
            dataKey="coverage_percent"
            name={coverageLabel}
            radius={[0, 6, 6, 0]}
            maxBarSize={24}
            fill="#8B5CF6"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const FacilityVolumeChart: React.FC<{
  data: HomeKpisFacilityComparisonItem[];
  patientsLabel: string;
  examsLabel: string;
  reportsLabel: string;
}> = ({ data, patientsLabel, examsLabel, reportsLabel }) => {
  if (!data?.length) {
    return <NoDataPlaceholder className="h-72" />;
  }
  const rows = data.slice(0, 12);

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 8, right: 18, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="facility_name"
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
            interval={0}
            angle={-18}
            textAnchor="end"
            height={54}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            wrapperStyle={{ fontSize: '12px', paddingBottom: '12px' }}
          />
          <Bar
            dataKey="total_patients"
            name={patientsLabel}
            fill={COLORS[0]}
            radius={[5, 5, 0, 0]}
            maxBarSize={28}
          />
          <Bar
            dataKey="total_studies"
            name={examsLabel}
            fill={COLORS[1]}
            radius={[5, 5, 0, 0]}
            maxBarSize={28}
          />
          <Bar
            dataKey="total_reports"
            name={reportsLabel}
            fill={COLORS[3]}
            radius={[5, 5, 0, 0]}
            maxBarSize={28}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CompactDistributionBars: React.FC<{
  data: { name: string; value: number }[];
  countLabel: string;
  colors?: string[];
}> = ({ data, countLabel, colors }) => {
  if (!data?.length) {
    return <NoDataPlaceholder className="h-64" />;
  }
  const rows = data.slice(0, 8);
  const palette = colors ?? COLORS;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 4, right: 18, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            interval={0}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Bar
            dataKey="value"
            name={countLabel}
            radius={[6, 6, 0, 0]}
            maxBarSize={42}
          >
            {rows.map((_entry, index) => (
              <Cell key={index} fill={palette[index % palette.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SimpleBarChart: React.FC<{
  data: ChartDatum[];
  label: string;
  color?: string;
  horizontal?: boolean;
  heightClassName?: string;
  maxItems?: number;
}> = ({
  data,
  label,
  color = '#3B82F6',
  horizontal = false,
  heightClassName = 'h-64',
  maxItems,
}) => {
  const rows = (maxItems ? data?.slice(0, maxItems) : data) || [];
  if (!rows.some((point) => point.value > 0)) {
    return <NoDataPlaceholder className={heightClassName} />;
  }

  return (
    <div className={heightClassName}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={
            horizontal
              ? { top: 4, right: 20, left: 72, bottom: 4 }
              : { top: 4, right: 18, left: -12, bottom: 0 }
          }
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={!horizontal}
            horizontal={horizontal}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                tick={{ fontSize: 12 }}
                stroke="currentColor"
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                className="text-gray-400"
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                stroke="currentColor"
                width={132}
                axisLine={false}
                tickLine={false}
                className="text-gray-500 dark:text-gray-400"
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                stroke="currentColor"
                axisLine={false}
                tickLine={false}
                className="text-gray-400"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke="currentColor"
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                className="text-gray-400"
              />
            </>
          )}
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Bar
            dataKey="value"
            name={label}
            radius={[6, 6, 0, 0]}
            fill={color}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const HourDayHeatmap: React.FC<{
  data: HomeKpisHeatmapPoint[];
  examsLabel: string;
}> = ({ data, examsLabel }) => {
  if (!data?.length) {
    return <NoDataPlaceholder className="h-[34rem]" />;
  }
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const counts = new Map(
    data.map((p) => [`${p.hour}-${p.day_of_week}`, p.count])
  );
  const max = Math.max(1, ...data.map((p) => p.count));

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[680px] grid-cols-[4rem_repeat(7,minmax(4rem,1fr))] gap-1 text-xs">
        <div />
        {days.map((day) => (
          <div
            key={day}
            className="py-1 text-center font-semibold text-gray-500"
          >
            {day}
          </div>
        ))}
        {Array.from({ length: 24 }, (_, hour) => (
          <React.Fragment key={hour}>
            <div className="py-1 pr-2 text-right font-medium text-gray-400">
              {String(hour).padStart(2, '0')}:00
            </div>
            {days.map((_day, dow) => {
              const value = counts.get(`${hour}-${dow}`) || 0;
              const alpha = value ? 0.14 + (value / max) * 0.72 : 0.04;
              return (
                <div
                  key={`${hour}-${dow}`}
                  className="rounded-md py-1.5 text-center font-semibold text-gray-700 dark:text-gray-100"
                  style={{ backgroundColor: `rgba(59, 130, 246, ${alpha})` }}
                  title={`${value} ${examsLabel}`}
                >
                  {value || ''}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export const GaDifferenceChart: React.FC<{
  data: HomeKpisGaPoint[];
  label: string;
}> = ({ data, label }) => {
  const rows = (data || []).map((point, index) => ({
    ...point,
    name: String(index + 1),
    value: point.difference_weeks,
  }));
  if (!rows.length) {
    return <NoDataPlaceholder className="h-80" />;
  }

  const colorOf = (value: number) => {
    const abs = Math.abs(value);
    if (abs <= 2) {
      return '#059669';
    }
    if (abs <= 5) {
      return '#D97706';
    }
    return '#DC2626';
  };

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 8, right: 18, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
            formatter={(value: number) => [`${value}w`, label]}
          />
          <Bar dataKey="value" name={label} radius={[3, 3, 0, 0]}>
            {rows.map((row, index) => (
              <Cell key={index} fill={colorOf(row.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const HabitsStackedChart: React.FC<{
  data: Record<string, { yes: number; no: number; unknown: number }>;
  labels: { yes: string; no: string; unknown: string };
}> = ({ data, labels }) => {
  const rows = Object.entries(data || {}).map(([name, values]) => ({
    name,
    ...values,
  }));
  if (!rows.length) {
    return <NoDataPlaceholder className="h-64" />;
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 8, right: 18, left: -12, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            className="text-gray-400"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <Tooltip
            contentStyle={tooltipStyle}
            wrapperClassName={tooltipClassName}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          <Bar
            dataKey="yes"
            stackId="habits"
            name={labels.yes}
            fill="#EF4444"
          />
          <Bar dataKey="no" stackId="habits" name={labels.no} fill="#10B981" />
          <Bar
            dataKey="unknown"
            stackId="habits"
            name={labels.unknown}
            fill="#CBD5E1"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const FacilityViewsChart: React.FC<{
  data: HomeKpisFacilityComparisonItem[];
  label: string;
}> = ({ data, label }) => {
  const rows = (data || [])
    .filter((facility) => facility.total_studies > 0)
    .map((facility) => ({
      name: facility.facility_name,
      value:
        Math.round(
          (facility.total_views_detected / facility.total_studies) * 10
        ) / 10,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);
  return (
    <SimpleBarChart
      data={rows}
      label={label}
      color="#06B6D4"
      horizontal
      heightClassName="h-72"
    />
  );
};
